
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mail, ArrowLeft, MailCheck, MailQuestion } from "lucide-react";
import { useTranslation } from "react-i18next";
import Hey from "@/lib/hey-utils";
import { trackAuthAction } from "@/utils/analytics";
import { use } from "i18next";
import { set } from "date-fns";

interface OtpLoginProps {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  email: string;
  setEmail: (email: string) => void;
  otpCode: string;
  setOtpCode: (code: string) => void;
  otpStep: "email" | "code";
  setOtpStep: (step: "email" | "code") => void;
  navigate: (path: string) => void;
  isValidEmail: (email: string) => boolean;
}

export const OtpLogin = ({
  isLoading,
  setIsLoading,
  email,
  setEmail,
  otpCode,
  setOtpCode,
  otpStep,
  setOtpStep,
  navigate,
  isValidEmail,
}: OtpLoginProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleOtpRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    console.debug('Starting OTP request...');

    try {
      const trimmedEmail = email.trim().toLowerCase();
      console.debug('Requesting OTP for email:', trimmedEmail);
      
      if (!isValidEmail(trimmedEmail)) {
        console.debug('Invalid email format:', trimmedEmail);
        throw new Error(t("auth.invalidEmail"));
      }

      console.debug('Invoking feasy-auth edge function...');
      trackAuthAction("otp_request");

      localStorage.setItem("otp_lastUsedUserEmail", trimmedEmail);

      let data = await Hey.HeyUtils.getInstance().requestOTP(trimmedEmail);
      if (!data?.success) {
        console.debug('Failed to request OTP:', data.error);
        trackAuthAction("otp_request", "failed", data.error);
        throw new Error(t("auth.otpRequestFailed"));
      }
      
      console.debug('OTP requested successfully');
      trackAuthAction("otp_request", "success");
      setOtpStep("code");
      toast({
        title: t("auth.codeSent"),
        description: t("auth.checkEmailForCode"),
      });
      let watchOtpCodeInterval = setInterval(async () => {
        // Watch localStorage for OTP code
        const storedEmail = localStorage.getItem("otp_userEmail");
        const storedCode = localStorage.getItem("otp_code");
        if (storedEmail && storedCode && storedEmail === trimmedEmail) {
          // TODO email/code are "crypted", check how to decrypt
          console.debug('OTP code found in localStorage:', storedCode);
          trackAuthAction("otp_code_from_button");
          setOtpCode(storedCode);
          localStorage.removeItem("otp_userEmail");
          localStorage.removeItem("otp_code");
          clearInterval(watchOtpCodeInterval);
          await handleOtpVerification(null);
        }
      }, 1000);
    } catch (error) {
      console.error('Error in handleOtpRequest:', error);
      toast({
        variant: "destructive",
        title: t("common.toast.error"),
        description: error.message || t("auth.unexpectedError"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerification = async (e: React.FormEvent) => {
    e?.preventDefault();
    setIsLoading(true);

    try {
      const trimmedEmail = email.trim().toLowerCase();
      trackAuthAction("otp_verify");
      
      let data = await Hey.HeyUtils.getInstance().validateOTP(otpCode, trimmedEmail);
      if (data?.success) {
        trackAuthAction("otp_verify", "success");
        navigate("/");
      } else {
        trackAuthAction("otp_verify", "failed", data.error);
        throw new Error(t("auth.sessionCreationFailed"));
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("common.toast.error"),
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setEmail(localStorage.getItem("otp_lastUsedUserEmail") || "");
  }, []);

  return (
    <div className="mt-8 space-y-4">
      {otpStep === "email" ? (
        <form onSubmit={handleOtpRequest} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="email"
              placeholder={t("settings.profile.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`pl-10 ${!isValidEmail(email) && email.length > 0 ? 'border-red-500' : ''}`}
              required
            />
            {email.length > 0 && !isValidEmail(email) && (
              <p className="mt-1 text-sm text-red-500">{t("auth.invalidEmail")}</p>
            )}
          </div>

          <Button 
            className="w-full" 
            type="submit" 
            disabled={isLoading || (email.length > 0 && !isValidEmail(email))}
            style={{ backgroundColor: "#000835" }}
          >
            <Mail className="w-4 h-4 mr-2" />
            {isLoading ? t("auth.sending") : t("auth.verifyEmail")}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleOtpVerification} className="space-y-4">
          <button
            type="button"
            onClick={() => setOtpStep("email")}
            className="flex items-center text-sm text-travel-500 hover:text-travel-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("auth.backToEmail")}
          </button>

          <div className="space-y-4 flex flex-col items-center">
            <p className="text-sm text-gray-600 text-center">
              {t("auth.enterVerificationCode")} {email}
            </p>
            <Input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              placeholder={t("auth.enter6DigitCode")}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-lg tracking-widest w-48"
              required
            />
          </div>

          <Button 
            className="w-full" 
            type="submit" 
            disabled={isLoading || otpCode.length !== 6}
            style={{ backgroundColor: "#000835" }}
          >
            <MailCheck className="w-4 h-4 mr-2" />
            {isLoading ? t("auth.verifying") : t("auth.validateOTP")}
          </Button>
        </form>
      )}
    </div>
  );
};
